import React, { useContext } from 'react';
import { EventDetailsContext } from '../../context/event_details.context';
import chunk from 'lodash/chunk';
import { Speaker } from '../../../types/event.types';
import { SocialMediaHandle } from '../../../types/event.types';
import Tooltip from '../../../components/react/tooltip/tooltip';
import styles from './speakers_section.module.scss';

const THUMBNAIL_DIMENSION = 215;

// eslint-disable-next-line max-len
const CLOUDINARY_PATH_FOR_THUMBNAIL = `https://res.cloudinary.com/devex/image/fetch/c_fill,f_auto,q_auto,g_face,h_${THUMBNAIL_DIMENSION},w_${THUMBNAIL_DIMENSION}/`;

const getSocialMediaIcon = (socialMedia: string): string => {
  if (socialMedia === 'Linkedin') return 'icon-linkedin';
  return `icon-${socialMedia.toLowerCase()}-square`;
};

const getSocialMediaPath = (socialMedia: string): string => {
  if (socialMedia === 'Linkedin') return 'https://www.linkedin.com/in';
  return `https://www.${socialMedia.toLowerCase()}.com`;
};

const SpeakersSection: React.FC = () => {
  const { speakers } = useContext(EventDetailsContext);

  const renderSocialMedia = (handles: SocialMediaHandle[]): JSX.Element[] => {
    return handles.map((handle) => {
      const socialMediaName = Object.keys(handle)[0];

      return (
        <Tooltip key={socialMediaName} placement='top' message={handle[socialMediaName]}>
          <a
            className='me-2 fs-3'
            key={socialMediaName}
            href={`${getSocialMediaPath(socialMediaName)}/${handle[socialMediaName]}`}
            aria-label={`${handle[socialMediaName]} ${socialMediaName} link`}
            target='_blank'
            rel='noreferrer'
          >
            <i className={`fab ${getSocialMediaIcon(socialMediaName)} ${styles.icon}`}></i>
          </a>
        </Tooltip>
      );
    });
  };

  const renderSpeakers: JSX.Element[] = chunk(speakers, 3).map((row: Speaker[], index: number) => (
    <div className='row mt-4' key={index}>
      {row.map((speaker: Speaker) => (
        <div className='col-md-4 col-12 my-3' key={speaker.id}>
          <div className={styles.thumbnailContainerMaxWidth}>
            {speaker.thumbnail ? (
              <img
                alt={`${speaker.full_name} thumbnail`}
                src={CLOUDINARY_PATH_FOR_THUMBNAIL + speaker.thumbnail}
              />
            ) : (
              <div
                className='placeholder placeholder-lg'
                data-testid={`${speaker.full_name} thumbnail placeholder`}
              >
                <i className='fal icon-user' />
              </div>
            )}
            <h3 className='my-25 fs-4'>
              {speaker.full_name}
            </h3>
            <span>{speaker.headline}</span>
            {speaker.social_media_handles.length > 0 && (
              <div className='mt-25'>{renderSocialMedia(speaker.social_media_handles)}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  ));

  return speakers.length ? (
    <div id='speakers' className={`container py-5 ${styles.speakers}`}>
      <div className='row'>
        <div className='col'>
          <h2 className='py-1 border-bottom'>Speakers</h2>
          {renderSpeakers}
        </div>
      </div>
    </div>
  ) : null;
};

export default SpeakersSection;
